import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { WarningAmberRounded as WarningIcon } from "@mui/icons-material";
import { authClient } from "../../lib/auth-client";
import { useDeleteUserAccount } from "../../hooks/useDeleteUserAccount";
import { MembershipStatus } from "../../types/lemonSqueezy";
import { PlanDialogShell } from "./PlanDialogShell";

const DESTRUCTIVE_RED = "#ef4444";
const DESTRUCTIVE_RED_HOVER = "#dc2626";

const ACTIVE_STATUSES = ["active", "on_trial", "cancelled"];

type DeleteAccountCardProps = {
  membership: MembershipStatus["membership"] | null;
};

/**
 * Account deletion entry point in Account Settings (App Store 5.1.1(v):
 * in-app deletion, web AND native). Deliberately quiet — a single muted
 * text button, no red panel (this is a finance app, not a dev tool); the
 * confirm dialog carries all the gravity: type DELETE, plus the password for
 * credential users (social users rely on the server's session-freshness
 * check). Lemon Squeezy subscriptions are cancelled by the server as part of
 * deletion; App Store subscriptions CANNOT be cancelled by anyone but the
 * user, so the dialog says so plainly — deliberately with no shortcut link
 * (informing is required, a shortcut is not).
 */
const DeleteAccountCard = ({ membership }: DeleteAccountCardProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);
  const deleteAccount = useDeleteUserAccount();

  const hasActiveSubscription =
    !!membership && ACTIVE_STATUSES.includes(membership.status);
  const isStoreBilled = membership?.billingSource === "revenuecat";

  // Credential users must re-enter their password (server-enforced); social
  // users have none, so the field only renders when a credential account
  // exists. If the lookup fails we start without the field — the server's
  // PASSWORD_REQUIRED error then reveals it (self-healing).
  useEffect(() => {
    if (!dialogOpen) return;
    authClient.listAccounts().then(({ data }) => {
      const hasCredential = (data ?? []).some(
        (account: { providerId?: string; provider?: string }) =>
          (account.providerId ?? account.provider) === "credential",
      );
      setNeedsPassword(hasCredential);
    });
  }, [dialogOpen]);

  useEffect(() => {
    if (
      deleteAccount.error instanceof Error &&
      /password/i.test(deleteAccount.error.message)
    ) {
      setNeedsPassword(true);
    }
  }, [deleteAccount.error]);

  const handleClose = () => {
    if (deleteAccount.isPending) return;
    setDialogOpen(false);
    setConfirmText("");
    setPassword("");
    deleteAccount.reset();
  };

  const confirmDisabled =
    confirmText.trim().toUpperCase() !== "DELETE" ||
    (needsPassword && password.length === 0) ||
    deleteAccount.isPending;

  let errorMessage: string | null = null;
  if (deleteAccount.error instanceof Error) {
    errorMessage = /session expired/i.test(deleteAccount.error.message)
      ? "For your security, please sign out, sign back in, and try again."
      : deleteAccount.error.message;
  }

  return (
    <>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setDialogOpen(true)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8125rem",
            px: 2,
            borderRadius: 2,
            color: DESTRUCTIVE_RED,
            borderColor: "rgba(239, 68, 68, 0.4)",
            "&:hover": {
              borderColor: DESTRUCTIVE_RED,
              backgroundColor: "rgba(239, 68, 68, 0.06)",
            },
          }}
        >
          Delete account
        </Button>
      </Box>

      <PlanDialogShell
        open={dialogOpen}
        onClose={handleClose}
        title="Delete your account"
        subtitle="This cannot be undone"
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(239, 68, 68, 0.1)",
            }}
          >
            <WarningIcon sx={{ fontSize: 30, color: DESTRUCTIVE_RED }} />
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            px: 1,
          }}
        >
          Your account and all of its data will be permanently deleted, and
          there is no way to recover it.
        </Typography>

        {hasActiveSubscription && !isStoreBilled && (
          <Alert severity="info" sx={{ mt: 2.5, borderRadius: 2 }}>
            Your subscription will be cancelled as part of deletion — you will
            not be billed again.
          </Alert>
        )}
        {hasActiveSubscription && isStoreBilled && (
          <Alert severity="warning" sx={{ mt: 2.5, borderRadius: 2 }}>
            Deleting your account does <strong>not</strong> cancel your App
            Store subscription. To stop future charges, cancel it first in
            your device&apos;s subscription settings (Settings → your name →
            Subscriptions).
          </Alert>
        )}

        <Box sx={{ mt: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
          {needsPassword && (
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Your password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={deleteAccount.isPending}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          )}

          <TextField
            fullWidth
            size="small"
            label='Type "DELETE" to confirm'
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            disabled={deleteAccount.isPending}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
          <Button
            fullWidth
            disabled={confirmDisabled}
            onClick={() =>
              deleteAccount.mutate(needsPassword ? password : undefined)
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9375rem",
              borderRadius: 2,
              py: 1.25,
              backgroundColor: DESTRUCTIVE_RED,
              color: "#ffffff",
              "&:hover": { backgroundColor: DESTRUCTIVE_RED_HOVER },
              "&:disabled": {
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            {deleteAccount.isPending && (
              <CircularProgress size={18} color="inherit" />
            )}
            {!deleteAccount.isPending && "Permanently delete account"}
          </Button>
          <Button
            fullWidth
            onClick={handleClose}
            disabled={deleteAccount.isPending}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              color: theme.palette.text.secondary,
            }}
          >
            Keep my account
          </Button>
        </Box>
      </PlanDialogShell>
    </>
  );
};

export default DeleteAccountCard;
